import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useToast } from '../ui/use-toast';
import { Settings, Check, X, UserPlus, UserCheck, UserX, Users } from 'lucide-react';
import { staffService } from '../../services/staff';
import { getGymAdminBaseUrl } from '../../utils/gym-admin-url';
import type { UserStaff } from '../../types/user';

/**
 * The kinds of place a person can be on the staff of from the member app. It
 * picks the wording ("this gym", "this circuit", "this competition") and where
 * an admin is sent to manage the place.
 */
export type StaffPlaceKind = 'gym' | 'trophy' | 'competition';

interface GymStaffActionsProps {
  /** Id of the location the staff row is on. */
  gymId: string;
  gymSlug: string;
  currentUserStaff: UserStaff | null;
  /** Defaults to a gym, which is every caller from before circuits existed. */
  kind?: StaffPlaceKind;
}

/**
 * Where an admin manages the place. A gym and a competition have a settings
 * page in the member app; a circuit is run only from the admin panel, on its
 * own host — so that one leaves the app instead of routing.
 */
function manageUrl(kind: StaffPlaceKind, slug: string): { to: string } | { href: string } {
  switch (kind) {
    case 'trophy':
      return { href: `${getGymAdminBaseUrl()}/${slug}/stages` };
    case 'competition':
      return { to: `/competitions/${slug}/settings` };
    default:
      return { to: `/gyms/${slug}/settings` };
  }
}

export default function GymStaffActions({ gymId, gymSlug, currentUserStaff, kind = 'gym' }: GymStaffActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isStaffAdmin = currentUserStaff?.staff_roles.includes('admin') && currentUserStaff?.status === 'confirmed';
  const staffStatus = currentUserStaff?.status;

  // Se l'utente non ha nessun ruolo staff per questo luogo, non mostrare nulla
  if (!currentUserStaff) {
    return null;
  }

  // The strings that name the kind of place carry a `_trophy` / `_competition`
  // variant; the plain key is the gym's, and the fallback for anything else.
  const tp = (key: string) => t(`gymProfile.staff.${key}`, { context: kind });

  const handleGoToSettings = () => {
    const target = manageUrl(kind, gymSlug);
    if ('href' in target) {
      window.location.assign(target.href);
    } else {
      navigate(target.to);
    }
  };

  const handleConfirmInvitation = async () => {
    try {
      await staffService.confirmInvitation(gymId);
      toast({
        title: t('gymProfile.staff.invitationConfirmed'),
        description: tp('invitationConfirmedDescription'),
      });
      // Reload page to update user staff status
      window.location.reload();
    } catch (error) {
      toast({
        title: t('gymProfile.staff.errorConfirming'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const handleRejectInvitation = async () => {
    try {
      await staffService.rejectInvitation(gymId);
      toast({
        title: t('gymProfile.staff.invitationRejected'),
        description: t('gymProfile.staff.invitationRejectedDescription'),
      });
      // Reload page to update user staff status
      window.location.reload();
    } catch (error) {
      toast({
        title: t('gymProfile.staff.errorRejecting'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveStaff = async () => {
    try {
      await staffService.leaveStaff(gymId);
      toast({
        title: t('gymProfile.staff.leftStaff'),
        description: tp('leftStaffDescription'),
      });
      // Reload page to update user staff status
      window.location.reload();
    } catch (error) {
      toast({
        title: t('gymProfile.staff.errorLeaving'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const getStatusContent = () => {
    switch (staffStatus) {
      case 'confirmed':
        if (isStaffAdmin) {
          return {
            icon: <Settings className="h-4 w-4" />,
            title: t('gymProfile.staff.adminAccess'),
            description: tp('adminDescription'),
            actions: (
              <Button
                variant="default"
                onClick={handleGoToSettings}
                className="w-full sm:w-auto"
              >
                <Settings className="h-4 w-4 mr-2" />
                {t('gymProfile.settings')}
              </Button>
            )
          };
        } else {
          return {
            icon: <UserCheck className="h-4 w-4" />,
            title: t('gymProfile.staff.staffMember'),
            description: tp('staffDescription'),
            actions: (
              <Button
                variant="outline"
                onClick={handleRemoveStaff}
                className="w-full sm:w-auto"
              >
                <UserX className="h-4 w-4 mr-2" />
                {t('gymProfile.staff.leaveStaff')}
              </Button>
            )
          };
        }

      case 'pending':
        return {
          icon: <Users className="h-4 w-4" />,
          title: t('gymProfile.staff.pendingInvitation'),
          description: tp('pendingDescription'),
          actions: (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="default"
                onClick={handleConfirmInvitation}
                className="w-full sm:w-auto"
              >
                <Check className="h-4 w-4 mr-2" />
                {t('gymProfile.staff.confirmInvitation')}
              </Button>
              <Button
                variant="outline"
                onClick={handleRejectInvitation}
                className="w-full sm:w-auto"
              >
                <X className="h-4 w-4 mr-2" />
                {t('gymProfile.staff.rejectInvitation')}
              </Button>
            </div>
          )
        };

      case 'rejected':
        return {
          icon: <UserX className="h-4 w-4" />,
          title: t('gymProfile.staff.rejectedRequest'),
          description: t('gymProfile.staff.rejectedDescription'),
          actions: (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="default"
                onClick={handleConfirmInvitation}
                className="w-full sm:w-auto"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('gymProfile.staff.acceptRequest')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemoveStaff}
                className="w-full sm:w-auto"
              >
                <X className="h-4 w-4 mr-2" />
                {t('gymProfile.staff.removeRequest')}
              </Button>
            </div>
          )
        };

      default:
        return null;
    }
  };

  const statusContent = getStatusContent();

  if (!statusContent) {
    return null;
  }

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardContent className="pt-4">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-md">
            {statusContent.icon}
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="font-medium text-foreground">{statusContent.title}</h3>
              <p className="text-sm text-muted-foreground">{statusContent.description}</p>
            </div>
            <div className="flex justify-start">
              {statusContent.actions}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
